import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Divider,
  Typography,
  Box,
  Avatar,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Business,
  Work,
  Description as DescriptionIcon,
  School,
  Language,
  Share,
  Image as ImageIcon,
  Close,
  Email,
  Phone
} from '@mui/icons-material';

const EditEventOrganizerDetails = ({ onSave, organizer, isEditable }) => {
  const [selectedImage, setSelectedImage] = useState();
  const [organizerData, setOrganizerData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    bio: '',
    isOrganization: false,
    organization: '',
    organizationRole: '',
    expertise: '',
    socialMediaLink: '',
    image: null
  });

  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (organizer) {
      setOrganizerData({
        name: organizer.name || '',
        email: organizer.email || '',
        phone: organizer.phone || '',
        website: organizer.website || '',
        bio: organizer.bio || '',
        isOrganization: organizer.organizerIsOrganization,
        organization: organizer.organization || '',
        organizationRole: organizer.organizationRole || '',
        expertise: organizer.expertise || '',
        socialMediaLink: organizer.socialMediaLink || '',
        image: organizer.image || null
      });

      // Set image preview only if organizer is an organization and has an image
      if (organizer.organizerIsOrganization && organizer.image) {
        setImagePreview(organizer.image);
      }
    }
  }, [organizer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrganizerData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (e) => {
    const isOrganization = e.target.value === 'organization';
    setOrganizerData(prev => ({
      ...prev,
      isOrganization,
      organization: isOrganization ? prev.organization : '',
      organizationRole: isOrganization ? prev.organizationRole : ''
    }));
    
    // Clear image preview when switching to individual (non-organization)
    if (!isOrganization) {
      setImagePreview(null);
      setSelectedImage(null);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Create a preview URL for the uploaded file
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setOrganizerData(prev => ({ ...prev, image: file }));
    }
  };

  const handleImageRemove = () => {
    setImagePreview(null);
    setSelectedImage(null);
    setOrganizerData(prev => ({ ...prev, image: null }));
  };

  const handleSave = () => {
    const formData = new FormData();

    // Append organizer data
    formData.append('organizerIsOrganization', organizerData.isOrganization);
    formData.append('organization', organizerData.organization);
    formData.append('organizationRole', organizerData.organizationRole);
    formData.append('bio', organizerData.bio);
    formData.append('organizationWebsite', organizerData.website);
    formData.append('socialMediaLink', organizerData.socialMediaLink);

    // Append organizer image only if it's an organization and an image was selected
    if (organizerData.isOrganization && selectedImage) {
      formData.append('image', selectedImage);
    }

    // If onSave prop is provided, call it with the data
    if (onSave) {
      onSave(formData);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1 }} /> Organizer Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormControl component="fieldset" sx={{ mb: 4 }}>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 500 }}>
            Are you creating this event for an organization?
          </FormLabel>
          <RadioGroup
            row
            name="isOrganization"
            value={organizerData.isOrganization ? 'organization' : 'individual'}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              value="organization"
              control={<Radio color="primary" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ mr: 1 }} />
                  Yes, representing an organization
                </Box>
              }
            />
            <FormControlLabel
              value="individual"
              control={<Radio color="primary" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  No, as an individual
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {organizerData.isOrganization && (
          <>
            <TextField
              fullWidth
              label="Organization Name"
              name="organization"
              value={organizerData.organization}
              onChange={handleInputChange}
              required
              sx={{ mb: 3 }}
              variant="outlined"
              disabled={isEditable}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Your Role in the Organization"
              name="organizationRole"
              value={organizerData.organizationRole}
              onChange={handleInputChange}
              required
              sx={{ mb: 3 }}
              variant="outlined"
              disabled={isEditable}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Work color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}

        <TextField
          fullWidth
          label="Bio"
          name="bio"
          value={organizerData.bio}
          onChange={handleInputChange}
          multiline
          rows={4}
          required
          sx={{ mb: 3 }}
          variant="outlined"
          helperText="Tell us about yourself or your organization"
          disabled={isEditable}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DescriptionIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Expertise"
          name="expertise"
          value={organizerData.expertise}
          onChange={handleInputChange}
          multiline
          sx={{ mb: 3 }}
          variant="outlined"
          helperText="Your areas of expertise or the organization's focus areas"
          disabled={isEditable}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <School color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={organizerData.website}
            onChange={handleInputChange}
            variant="outlined"
            helperText="Optional"
            disabled={isEditable}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Language color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Social Media Link"
            name="socialMediaLink"
            value={organizerData.socialMediaLink}
            onChange={handleInputChange}
            variant="outlined"
            helperText="Optional"
            disabled={isEditable}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Share color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Only show image upload section for organizations */}
        {organizerData.isOrganization && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Organization Logo
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="organizer-image-upload"
                type="file"
                onChange={handleImageUpload}
              />
              <label htmlFor="organizer-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  color="primary"
                  sx={{ mb: 2 }}
                >
                  Upload Logo
                </Button>
              </label>

              {(imagePreview || organizerData.image) && (
                <Box sx={{ position: 'relative', width: 200, mt: 2 }}>
                  <Avatar
                    src={imagePreview || organizerData.image}
                    variant="rounded"
                    sx={{ width: '100%', height: 120, borderRadius: 2 }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.7)',
                      },
                    }}
                    onClick={handleImageRemove}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          fullWidth
          size="large"
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
};

export default EditEventOrganizerDetails;